import {
  RecipientCard,
  RecipientSkeletonCard,
} from "./recipient-card/recipient-card";
import { Grid } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import React, { useEffect, useState } from "react";
import {
  changePagination,
  getMyRecipients,
} from "../../../../redux/slices/recipients.slice";
import BarekamText from "../../../components/text";
import Message from "../../../components/message";
import { MessageSections } from "../../../components/message/message";
import { getRecipientDocumentTypes } from "../../../../redux/slices/static-data.slice";
import { ToastContainer } from "react-toastify";
import { ActionBarWithSaveButton } from "../user-actionbar/action-bar-with-save-button";
import BarekamPagination from "../../../orders/my/pagination/barekam-pagination";
import EditRecipient from "./edit-recipient";
import { Recipient } from "../../../shared/models/user/recipient/recipient";
import { EditDefaultRecipient } from "./edit-default-recipient/edit-default-recipient";
import { StandardDialog } from "../../../components/dialogs/standard-dialog/standard-dialog";

export const Recipients = () => {
  const [recipientToEdit, setRecipientToEdit] = useState<
    Recipient | undefined
  >();
  const [defaultRecipientDialogOpened, setDefaultRecipientDialogOpened] =
    useState(false);

  const { myRecipientsData, isLoading } = useAppSelector(
    (state) => state.recipientDataReducer,
  );
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);

  const dispatch = useAppDispatch();

  useEffect(() => {
    Promise.allSettled([
      dispatch(getMyRecipients({ forceLoad: false })),
      dispatch(getRecipientDocumentTypes()),
    ]).then(() => void 0);
  }, []);

  function handleEditRecipientClose(recipient: Recipient | null) {
    if (!!recipient) {
      dispatch(getMyRecipients({ forceLoad: true }));
    }

    setRecipientToEdit(undefined);
  }

  function handlePageChange(event: unknown, newPage: number) {
    dispatch(
      changePagination({
        pageSize: myRecipientsData.pageSize,
        currentPage: newPage - 1,
      }),
    );
    dispatch(getMyRecipients({ forceLoad: true, page: newPage - 1 }));
  }

  function handleAddNewRecipient() {
    setRecipientToEdit({} as Recipient);
  }

  const PrimaryActionText = () => (
    <>
      <BarekamText variant={"xxl"} width={"unset"}>
        +
      </BarekamText>

      <BarekamText variant={"m"} bold>
        <Message
          messageKey={"add_new_recipient"}
          sectionId={MessageSections.USER}
        />
      </BarekamText>
    </>
  );

  const SecondaryActionText = () => (
    <BarekamText variant={"m"} bold>
      <Message
        messageKey={"change_default_recipient"}
        sectionId={MessageSections.USER}
      />
    </BarekamText>
  );

  return (
    <>
      <ActionBarWithSaveButton
        primaryActionText={<PrimaryActionText />}
        secondaryActionText={<SecondaryActionText />}
        onSecondaryActionClick={() => setDefaultRecipientDialogOpened(true)}
        onPrimaryActionClick={handleAddNewRecipient}
        disabled={!isLoading}
      />

      {isLoading ? (
        <RecipientSkeletonCard />
      ) : (
        <Grid
          container
          gridTemplateColumns={"repeat(auto-fill, minmax(22rem, 1fr))"}
          display={"grid"}
          rowGap={"1rem"}
          columnGap={"1rem"}
          padding={matchesPhone ? "0rem" : "1rem"}
        >
          {myRecipientsData.recipients.map((recipient, index) => (
            <RecipientCard
              recipient={recipient}
              setRecipientToEdit={setRecipientToEdit}
              onClick={() =>
                (index != 0 || myRecipientsData.currentPage > 0) &&
                setRecipientToEdit(recipient)
              }
              key={recipient.recipientId}
            />
          ))}
        </Grid>
      )}

      <StandardDialog
        headerLabel={
          <Message
            messageKey={
              recipientToEdit?.recipientId
                ? "edit_recipient"
                : "add_new_recipient"
            }
            sectionId={MessageSections.USER}
          />
        }
        open={!!recipientToEdit}
      >
        <EditRecipient
          sx={{ boxShadow: "unset" }}
          recipient={(recipientToEdit as Recipient) || {}}
          recipientSaved={handleEditRecipientClose}
          popupClosed={() => handleEditRecipientClose(null)}
        />
      </StandardDialog>

      <StandardDialog
        headerLabel={
          <Message
            messageKey={"select_default_recipient"}
            sectionId={MessageSections.USER}
          />
        }
        open={defaultRecipientDialogOpened}
      >
        <EditDefaultRecipient
          onClose={(withChange) => {
            withChange && dispatch(getMyRecipients({ forceLoad: true }));
            setDefaultRecipientDialogOpened(false);
          }}
        />
      </StandardDialog>

      {myRecipientsData.totalItems > myRecipientsData.pageSize ? (
        <BarekamPagination
          {...myRecipientsData}
          onPageChange={handlePageChange}
        />
      ) : (
        <></>
      )}

      <ToastContainer />
    </>
  );
};
